/**
 * Detects annotation comments that are only a generic verdict or phrase.
 *
 * Normalization is applied to a comparison copy so the reviewer's typed value
 * is never rewritten.
 * @module genericCommentValidation
 */

/** Shown under Comments when {@link isGenericComment} is true. */
export const GENERIC_COMMENT_WARNING_MESSAGE =
  "This comment may be too generic to improve the AI. Identify the specific document evidence, applicable criterion or guideline, and reason for your decision.";

const GENERIC_VERDICTS = new Set([
  "met",
  "not met",
  "correct",
  "wrong",
  "incorrect",
]);

const FILLER_WORDS = new Set([
  "a",
  "an",
  "the",
  "this",
  "that",
  "it",
  "is",
  "was",
  "were",
  "are",
  "be",
  "been",
  "just",
  "only",
  "simply",
  "yes",
  "no",
  "please",
  "so",
  "very",
  "really",
  "quite",
  "basically",
  "overall",
]);

const GENERIC_PHRASE_PATTERNS = [
  /^as per documents available in oncoemr$/,
  /^as per nccn$/,
  /^\d+ criterion not met$/,
  /^\d+ criteria not met$/,
  /^all \d+ criteria were met$/,
  /^all \d+ criteria met$/,
];

const TRAILING_PUNCTUATION = /[.:,;!?]+$/;

/**
 * Builds a comparison string from a comment without mutating the original.
 *
 * @input comment - Raw comment text from the reviewer
 * @return Lowercased, trimmed text with collapsed whitespace and no trailing punctuation
 */
export const normalizeCommentForValidation = (comment: string): string => {
  const collapsed = comment
    .replace(/[\s\u00a0]+/g, " ")
    .trim()
    .toLowerCase();
  return collapsed.replace(TRAILING_PUNCTUATION, "").trim();
};

/**
 * Strips documented filler words so a generic phrase wrapped in them still warns.
 *
 * @input normalized - Already-normalized comment text
 * @return Remaining words joined by a single space, or an empty string
 */
const stripFillerWords = (normalized: string): string =>
  normalized
    .split(" ")
    .filter((word) => word.length > 0 && !FILLER_WORDS.has(word))
    .join(" ");

/**
 * Returns whether a normalized string is an exact listed verdict or phrase.
 *
 * @input normalized - Already-normalized comment text
 * @return True when the text is a generic verdict or phrase
 */
const matchesGenericPattern = (normalized: string): boolean => {
  if (!normalized) return false;
  if (GENERIC_VERDICTS.has(normalized)) return true;
  return GENERIC_PHRASE_PATTERNS.some((pattern) => pattern.test(normalized));
};

/**
 * Returns whether a comment is too generic to improve the AI.
 *
 * @input comment - Raw comment text from the reviewer
 * @return True when the comment is only, or substantially only, a listed warning value
 */
export const isGenericComment = (comment: string): boolean => {
  const normalized = normalizeCommentForValidation(comment);
  if (!normalized) return false;
  if (matchesGenericPattern(normalized)) return true;
  return matchesGenericPattern(stripFillerWords(normalized));
};
